import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { bookmarksAPI } from '../api/client';
import { extractErrorMessage } from '../utils/errors';

const UNCATEGORIZED = '__uncategorized__';

/**
 * The Profile page's bookmark manager (GitHub issue #59): grouped by folder,
 * with folder create/rename/delete and per-bookmark move/remove.
 *
 * Self-contained (owns its own fetching and API calls) rather than
 * presentational-with-a-parent-owning-state like DelegationsPanel -- there is
 * only one caller (ProfilePage) and, unlike delegations, no admin-facing
 * counterpart that would need the same list+form driven from different
 * endpoints. Nothing here is a security boundary; every endpoint it calls is
 * self-scoped from the session.
 */
function BookmarksPanel() {
  const [bookmarks, setBookmarks] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const load = useCallback(() => {
    setError(null);
    Promise.all([bookmarksAPI.list(), bookmarksAPI.listFolders()])
      .then(([bRes, fRes]) => {
        setBookmarks(bRes.data);
        setFolders(fRes.data);
      })
      .catch((err) => setError(extractErrorMessage(err)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreateFolder = async (e) => {
    e.preventDefault();
    if (!newFolderName.trim()) return;
    try {
      await bookmarksAPI.createFolder(newFolderName.trim());
      setNewFolderName('');
      load();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const handleRenameFolder = async (folderId) => {
    if (!renameValue.trim()) return;
    try {
      await bookmarksAPI.renameFolder(folderId, renameValue.trim());
      setRenamingId(null);
      load();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const handleDeleteFolder = async (folderId) => {
    // Un-files its bookmarks (server-side ON DELETE SET NULL) -- they survive
    // as Uncategorized, never destroyed. No confirm dialog: nothing is lost.
    try {
      await bookmarksAPI.deleteFolder(folderId);
      load();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const handleMove = async (bookmarkId, folderIdValue) => {
    const folderId = folderIdValue === UNCATEGORIZED ? null : Number(folderIdValue);
    try {
      await bookmarksAPI.move(bookmarkId, folderId);
      load();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const handleRemove = async (bookmarkId) => {
    try {
      await bookmarksAPI.remove(bookmarkId);
      load();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  if (loading) {
    return <div className="loading">Loading bookmarks...</div>;
  }

  const groups = [
    ...folders.map((f) => ({ id: f.id, name: f.name })),
    { id: null, name: 'Uncategorized' },
  ];

  return (
    <div>
      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleCreateFolder} style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <input
          type="text"
          placeholder="New folder name"
          value={newFolderName}
          onChange={(e) => setNewFolderName(e.target.value)}
          style={{ flex: 1 }}
        />
        <button type="submit" className="btn btn-secondary btn-small">+ Add Folder</button>
      </form>

      {/* The hint sits ABOVE the groups rather than instead of them: a folder
          created before anything has been bookmarked is real, and rendering it
          only once something is filed in it made "+ Add Folder" look broken
          (GitHub issue #70). */}
      {bookmarks.length === 0 && (
        <div className="empty-state" style={{ padding: '12px 0' }}>
          No bookmarks yet. Open a policy and click "☆ Bookmark" to pin it here.
        </div>
      )}

      {(bookmarks.length > 0 || folders.length > 0) && (
        groups.map((group) => {
          const rows = bookmarks.filter((b) => (b.folder_id ?? null) === group.id);
          // An empty real folder still renders -- it's real, and rename/delete
          // must stay reachable. An empty Uncategorized group is only skipped
          // when at least one real folder exists, so it isn't just noise
          // beside them; with no folders at all it's the only group there is.
          if (rows.length === 0 && group.id === null && folders.length > 0) {
            return null;
          }
          return (
            <div key={group.id ?? 'uncategorized'} style={{ marginBottom: '18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                {group.id !== null && renamingId === group.id ? (
                  <>
                    <input
                      type="text"
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      style={{ fontWeight: 'bold' }}
                    />
                    <button className="btn btn-primary btn-small" onClick={() => handleRenameFolder(group.id)}>
                      Save
                    </button>
                    <button className="btn btn-secondary btn-small" onClick={() => setRenamingId(null)}>
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <strong>📁 {group.name}</strong>
                    {group.id !== null && (
                      <>
                        <button
                          className="btn btn-secondary btn-small"
                          onClick={() => { setRenamingId(group.id); setRenameValue(group.name); }}
                        >
                          Rename
                        </button>
                        <button
                          className="btn btn-danger btn-small"
                          onClick={() => handleDeleteFolder(group.id)}
                        >
                          Delete folder
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
              {rows.length === 0 ? (
                <div style={{ fontSize: '12px', color: 'var(--muted)' }}>Empty</div>
              ) : (
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Status</th>
                        <th>Location</th>
                        {/* No "Folder" header: the folder name is already this
                            group's own heading. The picker below is the move
                            control, not a column of data. */}
                        <th></th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((b) => (
                        <tr key={b.id}>
                          <td>
                            <Link to={`/documents/${b.document_id}`}><strong>{b.document_title}</strong></Link>
                            <div style={{ fontSize: '12px', color: 'var(--muted)' }}>{b.document_version_label}</div>
                          </td>
                          <td><span className={`badge badge-${b.document_status}`}>{b.document_status}</span></td>
                          <td>{b.document_hierarchy_display_path || '-'}</td>
                          <td>
                            <select
                              value={b.folder_id ?? UNCATEGORIZED}
                              onChange={(e) => handleMove(b.id, e.target.value)}
                            >
                              <option value={UNCATEGORIZED}>Uncategorized</option>
                              {folders.map((f) => (
                                <option key={f.id} value={f.id}>{f.name}</option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <button className="btn btn-danger btn-small" onClick={() => handleRemove(b.id)}>
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

export default BookmarksPanel;
